import { matches } from '../lib/match';
import type { KeyboardEvent as KeyEvent, ReactElement, ReactNode } from 'react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { Icon } from './Icon';

/** A modal — the working shape: a title, a close, the content, the buttons. */
export function Modal({
  title,
  onClose,
  children,
  foot,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  foot?: ReactNode;
}): ReactElement {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="float modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2 className="t-heading">{title}</h2>
          <Button variant="quiet" size="sm" onClick={onClose} aria-label="Close">
            <Icon name="close" size={15} />
          </Button>
        </div>
        {/* ⚠ The body scrolls, the head and the foot do not: a dialog longer
            than the window used to run off the bottom with its button
            ("i cant click the button to post in this view", 2026-09-24). */}
        <div className="modal-body">{children}</div>
        {foot ? <div className="modal-foot">{foot}</div> : null}
      </div>
    </div>
  );
}

/**
 * Destructive confirm — says it twice: the shape, the colour and the
 * sentence agree, and the button repeats the noun so a mis-click cannot be a
 * misread.
 */
export function Confirm({
  title,
  noun,
  onConfirm,
  onClose,
  working,
  children,
}: {
  title: string;
  noun: string;
  onConfirm: () => void;
  onClose: () => void;
  working?: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <Modal
      title={title}
      onClose={onClose}
      foot={
        <>
          <Button onClick={onClose}>Keep it</Button>
          <Button variant="danger" onClick={onConfirm} working={working ? `${noun}…` : false}>
            {noun}
          </Button>
        </>
      }
    >
      <p className="t-body">{children}</p>
    </Modal>
  );
}

/** The button a menu hangs from, when it is not the plain one. */
export interface MenuTrigger {
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /** A field-shaped button takes a width; the menu is at least as wide. */
  width?: number | string;
  /** The value in force, on the button — what a test and a screen reader read. */
  value?: string;
  title?: string;
}

export interface MenuItem {
  label: string;
  /**
   * What tells two rows apart when their labels are the same — two Asana
   * projects called "Sprint board" in two teams. The label is the fallback.
   */
  id?: string;
  onSelect?: () => void;
  danger?: boolean;
  /** A disabled item says why instead of just being grey. */
  disabledBecause?: string;
  /** One line under the label — what choosing it means. */
  sub?: string;
  /** The value in force now: a tick and the green tint, so it survives greyscale. */
  current?: boolean;
}

/**
 * A menu on a button. Opens on click, closes on outside click, Escape, a
 * scroll or a resize. Rendered at the document's root at a fixed position,
 * so no card's rounded corners can clip it — the row menu on Open was cut
 * off by its column (Vladimir, 2026-09-18). Opens upwards near the bottom.
 */
/**
 * How many items before the menu grows a filter.
 *
 * ⚠ A menu is a list you read; past about a dozen it becomes a list you
 * SEARCH, and one that keeps growing with use — series, folders, spaces — will
 * pass that without anybody noticing the day it does. Vladimir, 2026-09-21:
 * "What will be with this select when I will visit 1000 on offs meetings?"
 */
const FILTER_OVER = 10;

export function MenuButton({
  label,
  items,
  align = 'right',
  size,
  variant = 'secondary',
  /** Items pinned below the filtered list — "+ New…", "None". Never filtered out. */
  fixed = [],
  /**
   * A search field however short the list: picking a PERSON is a search, even
   * among six (Vladimir, 2026-09-24: "In channels when adding user we need a
   * search").
   */
  search = false,
  trigger = {},
}: {
  label: ReactNode;
  items: MenuItem[];
  align?: 'left' | 'right';
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'quiet' | 'danger';
  fixed?: MenuItem[];
  search?: boolean;
  /** How the button itself looks and is named — `Picker` makes it a field. */
  trigger?: MenuTrigger;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [needle, setNeedle] = useState('');
  const [place, setPlace] = useState<{
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
    minWidth?: number;
  }>({});
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  /**
   * Where the panel goes: under the button, or above it when there is no room
   * below. The menu is fixed to the viewport, so this is measured from the
   * button rather than inherited from it.
   */
  const placeUnderTheButton = useCallback(() => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom;
    const upwards = below < 280 && r.top > below;
    setPlace({
      ...(upwards ? { bottom: window.innerHeight - r.top + 6 } : { top: r.bottom + 6 }),
      ...(align === 'right'
        ? { right: Math.max(8, window.innerWidth - r.right) }
        : // ⚠ Never past the window's edge: a picker near the right of a
          // phone opened a panel half off the screen.
          { left: Math.max(8, Math.min(r.left, window.innerWidth - Math.max(r.width, 220) - 8)) }),
      // At least as wide as a field-shaped button, so the list reads as its.
      minWidth: r.width,
    });
  }, [align]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const at = e.target as Node;
      if (!ref.current?.contains(at) && !menuRef.current?.contains(at)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    // ⚠ **Follow the button — do not close.** This used to close the menu on
    // any scroll or resize, which sounds like the usual rule and is not: a
    // scroll event fires when ANYTHING on the page changes its height, so a
    // section arriving from the server shut a menu the reader had just opened.
    // The meeting page found it — the series banner lands a moment after the
    // page does, and the series menu died under the pointer every time. It
    // closes only when the button itself has left the view, because a panel
    // floating over unrelated content is worse than one that goes away.
    const onMove = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight) setOpen(false);
      else placeUnderTheButton();
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, placeUnderTheButton]);
  const toggle = () => {
    if (!open) {
      placeUnderTheButton();
      setNeedle('');
    }
    setOpen((o) => !o);
  };
  /** Closed by a choice or by Escape: the keyboard goes back where it came from. */
  const close = () => {
    setOpen(false);
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
  };
  // Every typed word, anywhere in the label or the line under it, in any
  // order — see `lib/match.ts` for what a plain substring used to miss.
  const matching = items.filter((i) => matches(needle, i.label, i.sub));
  const searchable = search || items.length > FILTER_OVER;
  // Opened without a filter: the keyboard starts on the value in force, as a
  // select's does, or on the first thing that can be chosen.
  useEffect(() => {
    if (!open || searchable) return;
    const menu = menuRef.current;
    const at =
      menu?.querySelector<HTMLElement>('[role="menuitem"][aria-current="true"]:not(:disabled)') ??
      menu?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)');
    at?.focus();
  }, [open, searchable]);
  /** Up and down through what can be chosen — from the filter, too. */
  const onMenuKey = (e: KeyEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      // ⚠ This menu's, not the dialog's under it: Escape in a picker inside a
      // modal closed the modal as well.
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const all = [
      ...(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []),
    ];
    if (all.length === 0) return;
    e.preventDefault();
    const at = all.indexOf(document.activeElement as HTMLElement);
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? all.length - 1
          : e.key === 'ArrowDown'
            ? (at + 1) % all.length
            : at <= 0
              ? all.length - 1
              : at - 1;
    all[next].focus();
  };
  const ordered = [
    ...matching.filter((i) => !i.danger),
    ...matching.filter((i) => i.danger),
    ...fixed,
  ];
  return (
    <div
      ref={ref}
      style={{
        position: 'relative',
        display: trigger.width ? 'block' : 'inline-block',
        width: trigger.width,
        maxWidth: '100%',
      }}
    >
      <Button
        variant={variant}
        size={size}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={trigger.ariaLabel}
        disabled={trigger.disabled}
        data-value={trigger.value}
        title={trigger.title}
        className={[open ? 'pressed' : '', trigger.className ?? ''].filter(Boolean).join(' ')}
        style={trigger.width ? { width: '100%' } : undefined}
      >
        {label}
        <Icon name="chevron" size={12} rotate={90} className="mb-caret" />
      </Button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="float menu"
              role="menu"
              onKeyDown={onMenuKey}
              style={{ position: 'fixed', zIndex: 60, ...place }}
            >
              {searchable ? (
                <div className="menu-filter">
                  <input
                    autoFocus
                    value={needle}
                    onChange={(e) => setNeedle(e.target.value)}
                    placeholder={`Filter ${items.length}…`}
                    aria-label="Filter"
                    onKeyDown={(e) => {
                      // Enter takes the only thing left, which is what a person
                      // means by typing enough to leave one.
                      if (e.key !== 'Enter') return;
                      const only = matching.filter((i) => i.onSelect);
                      if (only.length !== 1) return;
                      close();
                      only[0].onSelect?.();
                    }}
                  />
                </div>
              ) : null}
              {searchable && matching.length === 0 ? (
                <div className="menu-none">Nothing matches “{needle}”.</div>
              ) : null}
              {ordered.map((item, n) => (
                // ⚠ Not the label alone: two rows with the same words — two
                // projects of one name in two Asana teams — shared a key, and
                // React quietly dropped or swapped one of them.
                <Fragment key={item.id ?? `${n}:${item.label}`}>
                  {item.danger && n > 0 && !ordered[n - 1].danger ? (
                    <div className="menu-sep" />
                  ) : null}
                  <button
                    type="button"
                    role="menuitem"
                    className={`menu-item ${item.danger ? 'danger' : ''} ${item.current ? 'on' : ''} ${item.sub ? 'tall' : ''}`}
                    aria-current={item.current ? 'true' : undefined}
                    data-value={item.id}
                    disabled={Boolean(item.disabledBecause)}
                    onClick={() => {
                      close();
                      item.onSelect?.();
                    }}
                  >
                    <span className="stack" style={{ gap: 1, minWidth: 0 }}>
                      <span>
                        {item.label}
                        {item.current ? (
                          <span className="tick">
                            <Icon name="check" size={13} />
                          </span>
                        ) : null}
                      </span>
                      {item.sub ? <span className="sub">{item.sub}</span> : null}
                      {item.disabledBecause ? (
                        <span className="sub">{item.disabledBecause}</span>
                      ) : null}
                    </span>
                  </button>
                </Fragment>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
